import {
  Intercom,
  boot as bootIntercom,
  shutdown as shutdownIntercom,
  update as updateIntercom,
} from '@intercom/messenger-js-sdk';
import { captureException } from '@sentry/react';
import type { MeFragment } from '@wirechunk/lib/shared-queries/me-fragment.generated.ts';
import { apolloClient, CurrentUserContext, SessionStatus } from '@wirechunk/ui';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { use, useEffect, useRef } from 'react';
import { MeIntercomUserTokenDocument } from '../util/intercom/queries.generated.ts';

const { intercomAppId } = window.wirechunk;
let { intercomMessengerAuth } = window.wirechunk;

type IntercomBootSettings = Parameters<typeof Intercom>[0];
type IntercomMessengerUser = Pick<MeFragment, 'displayName' | 'email' | 'id'>;

type IntercomAuthenticatedBootSettings = IntercomBootSettings & {
  intercom_user_jwt?: string;
};

const createAnonymousSettings = (appId: string): IntercomBootSettings => ({
  app_id: appId,
});

const createAuthenticatedSettings = ({
  appId,
  user,
  intercomUserJwt,
}: {
  appId: string;
  user: IntercomMessengerUser;
  intercomUserJwt?: string;
}): IntercomAuthenticatedBootSettings => ({
  app_id: appId,
  user_id: user.id,
  email: user.email,
  name: user.displayName,
  intercom_user_jwt: intercomUserJwt,
});

const getIntercomUserToken = async (): Promise<string | null> => {
  if (intercomMessengerAuth?.enabled === false) {
    return null;
  }
  if (intercomMessengerAuth?.enabled) {
    const { userJwt } = intercomMessengerAuth;
    // Reset so that a user can sign out and sign back in.
    intercomMessengerAuth = undefined;
    return userJwt;
  }
  try {
    const { data } = await apolloClient.query({
      query: MeIntercomUserTokenDocument,
      fetchPolicy: 'no-cache',
    });
    return data.me?.intercomUserToken || null;
  } catch (error) {
    captureException(error);
    return null;
  }
};

export const IntercomProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { user, status } = use(CurrentUserContext);
  const hasInitialized = useRef(false);
  const bootedUserId = useRef<string | null>(null);
  const intercomRunning = useRef(false);
  const activeUserId = status === SessionStatus.Active ? user?.id : undefined;
  const activeUserEmail = status === SessionStatus.Active ? user?.email : undefined;
  const activeUserDisplayName = status === SessionStatus.Active ? user?.displayName : undefined;

  useEffect(() => {
    if (!intercomAppId) {
      return;
    }

    let canceled = false;

    const startIntercom = (settings: IntercomBootSettings): void => {
      if (hasInitialized.current) {
        bootIntercom(settings);
        return;
      }
      Intercom(settings);
      hasInitialized.current = true;
    };

    const syncIntercom = async () => {
      if (!activeUserId || !activeUserEmail || !activeUserDisplayName) {
        if (!intercomRunning.current) {
          startIntercom(createAnonymousSettings(intercomAppId));
          intercomRunning.current = true;
          bootedUserId.current = null;
          return;
        }
        if (bootedUserId.current !== null) {
          shutdownIntercom();
          startIntercom(createAnonymousSettings(intercomAppId));
          intercomRunning.current = true;
          bootedUserId.current = null;
        }
        return;
      }

      const intercomUserJwt = await getIntercomUserToken();
      if (canceled) {
        return;
      }

      if (!intercomRunning.current || bootedUserId.current !== activeUserId) {
        if (intercomRunning.current) {
          shutdownIntercom();
        }
        startIntercom(
          createAuthenticatedSettings({
            appId: intercomAppId,
            user: {
              id: activeUserId,
              email: activeUserEmail,
              displayName: activeUserDisplayName,
            },
            intercomUserJwt: intercomUserJwt || undefined,
          }),
        );
        intercomRunning.current = true;
        bootedUserId.current = activeUserId;
        return;
      }

      updateIntercom({
        user_id: activeUserId,
        email: activeUserEmail,
        name: activeUserDisplayName,
      });
    };

    void syncIntercom();

    return () => {
      canceled = true;
    };
  }, [activeUserDisplayName, activeUserEmail, activeUserId]);

  return children;
};
